import turfArea from '@turf/area';
import type { GeoJsonPolygon, GeoJsonMultiPolygon, UserPublic } from '@forest/contracts';
import { fetchNearestParcel } from './useParcels';

/**
 * Auto-populated fields for a freshly-drawn polygon. Any field may be empty
 * if we couldn't derive it — the caller only fills the form for non-empty
 * entries so the user can still type manually when auto-fill fails
 * (offline, Nominatim rate-limit, border coords, etc.).
 */
export type Autofill = {
  suprafata?: string;
  raion?: string;
  primarie?: string;
  entitateSilvica?: string;
  ocolSilvic?: string;
  ucf?: string;
};

/** Area in hectares, rounded to 4 decimals to match Prisma Decimal(10,4). */
export function polygonAreaHa(
  geom: GeoJsonPolygon | GeoJsonMultiPolygon,
): number {
  const m2 = turfArea(geom as unknown as GeoJSON.Geometry);
  if (!Number.isFinite(m2) || m2 <= 0) return 0;
  return Math.round((m2 / 10_000) * 10_000) / 10_000;
}

/**
 * Flatten any nested coordinate array into [lon, lat] tuples then take the
 * unweighted mean. Good enough for reverse-geocoding a polygon's nominal
 * location — we don't need a true centroid for this purpose.
 */
function polygonCentroid(
  geom: GeoJsonPolygon | GeoJsonMultiPolygon,
): { lat: number; lon: number } | null {
  const pts: Array<[number, number]> = [];
  const walk = (v: unknown) => {
    if (!Array.isArray(v)) return;
    if (typeof v[0] === 'number' && typeof v[1] === 'number') {
      pts.push([v[0] as number, v[1] as number]);
      return;
    }
    for (const c of v) walk(c);
  };
  walk(geom.coordinates);
  if (pts.length === 0) return null;
  const lon = pts.reduce((s, p) => s + p[0], 0) / pts.length;
  const lat = pts.reduce((s, p) => s + p[1], 0) / pts.length;
  return { lat, lon };
}

type NominatimResponse = {
  address?: {
    // Moldova's administrative hierarchy maps unevenly across OSM tags;
    // try the usual suspects in priority order at read time.
    county?: string;
    state_district?: string;
    state?: string;
    region?: string;
    municipality?: string;
    city?: string;
    town?: string;
    village?: string;
    hamlet?: string;
    suburb?: string;
    city_district?: string;
  };
};

/**
 * Reverse-geocode via OSM Nominatim. Never throws — on network error,
 * timeout, or malformed response it resolves to `{}` and the caller
 * silently falls back to user-profile defaults.
 *
 * Usage policy: Nominatim asks for a custom User-Agent and max 1 req/sec.
 * The User-Agent header cannot be set from browser fetch, but browsers
 * send their own UA which is distinctive enough. Calls are one-shot per
 * polygon save, well below the rate limit.
 */
async function reverseGeocode(
  lat: number,
  lon: number,
  lang: string,
  signal?: AbortSignal,
): Promise<{ raion?: string; primarie?: string }> {
  try {
    const url = new URL('https://nominatim.openstreetmap.org/reverse');
    url.searchParams.set('format', 'jsonv2');
    url.searchParams.set('lat', lat.toFixed(6));
    url.searchParams.set('lon', lon.toFixed(6));
    url.searchParams.set('zoom', '12');
    url.searchParams.set('accept-language', lang);
    url.searchParams.set('addressdetails', '1');

    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), 5000);
    const externalSignal = signal;
    const onExternalAbort = () => timeout.abort();
    externalSignal?.addEventListener('abort', onExternalAbort);

    let res: Response;
    try {
      res = await fetch(url.toString(), { signal: timeout.signal });
    } finally {
      clearTimeout(timer);
      externalSignal?.removeEventListener('abort', onExternalAbort);
    }
    if (!res.ok) return {};
    const body = (await res.json()) as NominatimResponse;
    const a = body.address ?? {};

    const raion = a.county ?? a.state_district ?? a.state ?? a.region ?? undefined;
    const primarie =
      a.municipality ??
      a.city ??
      a.town ??
      a.village ??
      a.hamlet ??
      a.suburb ??
      a.city_district ??
      undefined;

    return {
      ...(raion ? { raion: cleanAdminName(raion) } : {}),
      ...(primarie ? { primarie: cleanAdminName(primarie) } : {}),
    };
  } catch {
    return {};
  }
}

/**
 * OSM sometimes prefixes raion names with "Raionul " in Romanian data.
 * Strip the prefix for consistency with the imported xlsx values which
 * store the bare name (e.g. "Strășeni" not "Raionul Strășeni").
 */
function cleanAdminName(s: string): string {
  return s
    .replace(/^Raionul\s+/i, '')
    .replace(/^Municipiul\s+/i, '')
    .replace(/^Comuna\s+/i, '')
    .replace(/^Satul\s+/i, '')
    .replace(/^Orașul\s+/i, '')
    .trim();
}

/**
 * Normalize a free-text admin name for use inside a machine-readable key:
 *   - strip Romanian / Cyrillic diacritics via NFD decomposition
 *   - collapse runs of whitespace to a single `_`
 *   - drop characters that aren't alphanumeric, dash, or underscore
 *   - trim leading / trailing separators
 * Examples:
 *   "Strășeni"        -> "Straseni"
 *   "Chișinău"        -> "Chisinau"
 *   "Sat Bogăti"      -> "Sat_Bogati"
 *   "Единцы / Edineț" -> "Edincy_Edinet"
 */
export function normalizeKeyPart(raw: string): string {
  const decomposed = raw.normalize('NFD').replace(/[̀-ͯ]/g, '');
  return decomposed
    .replace(/ț/g, 't').replace(/Ț/g, 'T')
    .replace(/ș/g, 's').replace(/Ș/g, 'S')
    .replace(/\s+/g, '_')
    .replace(/[^A-Za-z0-9_\-]/g, '')
    .replace(/^[_\-]+|[_\-]+$/g, '');
}

/**
 * Build the `projectUcfKey` natural key from its component fields. The xlsx
 * pipeline imports these keys as `Cod_proiec-UCF`; when a user creates a
 * parcel from scratch we prefer a deterministic join of the four fields
 * that uniquely identify it in the cadastral sense: project id, mayoralty,
 * district, UCF. Empty parts are skipped (the user can still edit the key
 * manually if the auto-generated value doesn't fit their scheme).
 */
export function generateProjectUcfKey(parts: {
  projectId?: string;
  primarie?: string;
  raion?: string;
  ucf?: string;
}): string {
  const cleaned = [parts.projectId, parts.primarie, parts.raion, parts.ucf]
    .map((p) => (p ?? '').trim())
    .filter((p) => p.length > 0)
    .map(normalizeKeyPart)
    .filter((p) => p.length > 0);
  return cleaned.join('-');
}

/**
 * Build the `codProiect` value from its component fields, mirroring
 * `generateProjectUcfKey` but **without** the UCF tail. The natural
 * key is `<codProiect>-<UCF>`; the project code itself is just the
 * triplet (projectId / primarie / raion). Empty parts are skipped.
 */
export function generateCodProiect(parts: {
  projectId?: string;
  primarie?: string;
  raion?: string;
}): string {
  const cleaned = [parts.projectId, parts.primarie, parts.raion]
    .map((p) => (p ?? '').trim())
    .filter((p) => p.length > 0)
    .map(normalizeKeyPart)
    .filter((p) => p.length > 0);
  return cleaned.join('-');
}

/**
 * Build an autofill payload for a freshly-drawn polygon. Combines three
 * sources in descending priority for the admin fields:
 *   1. **Nearest existing parcel** (via `/api/parcels/nearest`) — ground
 *      truth from our own DB. If the drawn polygon's centroid falls
 *      INSIDE an existing parcel, that's the match (distance 0). Otherwise
 *      the closest neighbour by centroid distance. Only trusted up to
 *      25 km — past that the neighbour is likely in a different raion.
 *   2. **Reverse geocode via Nominatim** — OSM admin boundaries, used
 *      when the nearest-parcel check yields nothing usable (empty DB,
 *      too far, offline).
 *   3. **User-profile defaults** — final fallback.
 * `suprafata` is always computed from the polygon, unconditionally.
 */
export async function buildAutofill(
  geom: GeoJsonPolygon | GeoJsonMultiPolygon,
  user: UserPublic | null,
  lang: 'ro' | 'ru' | 'en' = 'ro',
  signal?: AbortSignal,
): Promise<Autofill> {
  const out: Autofill = {};

  const areaHa = polygonAreaHa(geom);
  if (areaHa > 0) out.suprafata = areaHa.toFixed(4);

  const center = polygonCentroid(geom);
  // First element of the multi-value list is the user's default entity (2026-04-30).
  const fallbackEntity = user?.assignedEntitatiSilvice?.[0];
  if (!center) {
    // No usable coords → just profile defaults.
    if (user?.defaultRaion) out.raion = user.defaultRaion;
    if (user?.defaultPrimarie) out.primarie = user.defaultPrimarie;
    if (fallbackEntity) out.entitateSilvica = fallbackEntity;
    if (user?.defaultOcolSilvic) out.ocolSilvic = user.defaultOcolSilvic;
    if (user?.defaultUcf) out.ucf = user.defaultUcf;
    return out;
  }

  // Ask both sources in parallel — they don't depend on each other.
  const [nominatim, nearest] = await Promise.all([
    reverseGeocode(center.lat, center.lon, lang, signal),
    fetchNearestParcel(center.lat, center.lon, signal),
  ]);

  // Nearest-parcel data is only trusted for raion/primarie when close or
  // when the point is actually inside the polygon. 25 km buffer covers a
  // typical Moldovan raion radius; past that, Nominatim is safer.
  const NEAR_KM = 25;
  const nearTrustable =
    nearest && (nearest.contains || nearest.distanceKm <= NEAR_KM);

  // Raion / primarie priority: nearest (trusted) > Nominatim > profile.
  out.raion =
    (nearTrustable ? nearest!.raion : undefined) ??
    nominatim.raion ??
    user?.defaultRaion ??
    undefined;
  out.primarie =
    (nearTrustable ? nearest!.primarie : undefined) ??
    nominatim.primarie ??
    user?.defaultPrimarie ??
    undefined;

  // Entitate silvica + ocol silvic: Nominatim doesn't know these, so
  // we try the nearest parcel first (always, even if far — forestry
  // units span large areas) and fall back to user profile (list[0]).
  out.entitateSilvica =
    (nearest?.entitateSilvica ?? undefined) ??
    fallbackEntity ??
    undefined;
  out.ocolSilvic =
    (nearest?.ocolSilvic ?? undefined) ??
    user?.defaultOcolSilvic ??
    undefined;

  // UCF is parcel-specific; profile default is a weaker hint than a
  // neighbour's UCF, but the neighbour's UCF is rarely the right one
  // for a *new* parcel. Prefer the profile default.
  if (user?.defaultUcf) out.ucf = user.defaultUcf;

  return out;
}
